import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { NavRow } from "./NavRow";
import { ProfileMenu } from "./ProfileMenu";
import { TailRows } from "./TailRows";
import { ADD_PATH, DESTINATIONS } from "./navItems";
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from "./layout";
import type { Destination } from "./routes";
import {
  CollapseNavIcon,
  ExpandNavIcon,
  PlusIcon,
} from "../../shared/components/icons";
import { usePreferencesStore } from "../../shared/store/preferences";
import { useT } from "../../shared/i18n";
import { useDemoMode } from "../demo/useDemoMode";

/**
 * Desktop shell nav: a 240px sidebar with labels, the primary Add, the four
 * destinations (Catalog, Browse, Items, Photos), the tail under a divider and
 * the account row at the bottom (spec §2; catalog-selector 02). The
 * double-arrow control collapses it to the 72px rail and the choice persists
 * as `sidebarCollapsed` in the preferences store.
 *
 * Only the item rows scroll: the account row and the expand/collapse control
 * are a pinned footer. With the whole nav as the scroll container the toggle
 * could fall below the fold of a short desktop viewport, which left the rail
 * with no reachable way back (scan-followups 01).
 */
export function NavSidebar({
  destination,
  onNavigate,
}: {
  destination: Destination | null;
  onNavigate: (path: string) => void;
}) {
  const t = useT();
  const demoMode = useDemoMode();
  const collapsed = usePreferencesStore((s) => s.sidebarCollapsed);
  const setSidebarCollapsed = usePreferencesStore((s) => s.setSidebarCollapsed);
  const showLabel = !collapsed;

  return (
    <Box
      component="nav"
      aria-label={t("primaryNav")}
      data-testid="nav-sidebar"
      sx={{
        width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: collapsed ? "center" : "stretch",
        borderRight: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        overflow: "hidden",
        transition: "width 0.2s",
        // No motion shipped without the user's consent (spec §8, §10).
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      <Box
        data-testid="nav-sidebar-scroll"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: collapsed ? "center" : "stretch",
        }}
      >
        {!demoMode && (
          <Box sx={{ p: collapsed ? 0 : 1.5, pt: 1.5, width: "100%" }}>
            {collapsed ? (
              <NavRow
                icon={PlusIcon}
                label={t("add")}
                orientation="horizontal"
                showLabel={false}
                tooltip
                testId="nav-add"
                onClick={() => onNavigate(ADD_PATH)}
              />
            ) : (
              <Button
                fullWidth
                variant="contained"
                startIcon={<PlusIcon fontSize="small" />}
                onClick={() => onNavigate(ADD_PATH)}
                data-testid="nav-add"
                sx={{ minHeight: 44, justifyContent: "flex-start" }}
              >
                {t("add")}
              </Button>
            )}
          </Box>
        )}

        {DESTINATIONS.map(({ destination: itemDestination, path, labelKey, icon }) => (
          <NavRow
            key={itemDestination}
            icon={icon}
            label={t(labelKey)}
            orientation="horizontal"
            showLabel={showLabel}
            tooltip={!showLabel}
            selected={itemDestination === destination}
            testId={`nav-${itemDestination}`}
            onClick={() => onNavigate(path)}
          />
        ))}

        <Box sx={{ flex: 1 }} />
        <Divider flexItem />
        <TailRows showLabel={showLabel} tooltip={!showLabel} onNavigate={onNavigate} />
      </Box>

      <Box
        data-testid="nav-sidebar-footer"
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          flexDirection: collapsed ? "column" : "row",
          gap: collapsed ? 0.5 : 1,
          py: 1,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <ProfileMenu variant={collapsed ? "avatar" : "row"} showLabel={showLabel} />
        <Tooltip title={t(collapsed ? "expandNav" : "collapseNav")}>
          <IconButton
            aria-label={t(collapsed ? "expandNav" : "collapseNav")}
            onClick={() => setSidebarCollapsed(!collapsed)}
            data-testid="sidebar-toggle"
            sx={{
              width: 44,
              height: 44,
              border: 1,
              borderColor: "divider",
              mr: collapsed ? 0 : 1.5,
            }}
          >
            {collapsed ? (
              <ExpandNavIcon fontSize="small" />
            ) : (
              <CollapseNavIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
